import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveNavigation from '../components/ResponsiveNavigation';
import RecruiterLayout from '../components/RecruiterLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SUPPORTED_LANGUAGES, persistLanguage, readStoredLanguage } from '../i18n';
import { 
  User, Building2, Calendar, Mail, Phone, MapPin, 
  Briefcase, Link2, CheckCircle, XCircle, ExternalLink,
  Settings as SettingsIcon, Shield, Send, PawPrint, Globe, Check as CheckIcon
} from 'lucide-react';
import { AVATARS, getAvatarUrl, DEFAULT_AVATAR_ID } from '../config/avatars';
import { JOB_BOARDS } from '../constants/jobBoards';
import BrandLogos from '../components/BrandLogos';
import CompanyJobBoardsCard from '../components/CompanyJobBoardsCard';

const Settings = () => {
  const { userProfile, session, refreshProfile } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Language dropdown state
  const currentLang = (
    readStoredLanguage()
    || i18n.resolvedLanguage
    || i18n.language
    || 'en'
  ).split('-')[0];
  const [langOpen, setLangOpen] = useState(false);
  const [langSaved, setLangSaved] = useState(false);
  const [langShowAll, setLangShowAll] = useState(false);
  const [, forceRerenderLang] = useState(0);

  const changeLanguage = async (code) => {
    if (!code || code === currentLang) { setLangOpen(false); return; }
    try {
      // persistLanguage: updates i18n + localStorage + cookie + Google Translate DOM
      persistLanguage(code);
      forceRerenderLang((n) => n + 1); // pick up localStorage change for non-hardcoded langs
      // Best-effort backend persist — the column may not exist yet.
      if (session?.access_token) {
        axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/profile/preferred-language`,
          { language: code },
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        ).catch(() => {});
      }
      setLangSaved(true);
      setTimeout(() => setLangSaved(false), 1600);
    } finally {
      setLangOpen(false);
    }
  };

  // Sync i18n with the user's server-side preference once the profile loads
  useEffect(() => {
    const server = (userProfile?.preferredLanguage || userProfile?.preferred_language || '').toLowerCase();
    if (server && server !== currentLang && SUPPORTED_LANGUAGES.some(l => l.code === server)) {
      persistLanguage(server);
    }
  }, [userProfile?.preferredLanguage, userProfile?.preferred_language]);

  // Avatar picker: which category tab is active (animals / humans)
  const [avatarTab, setAvatarTab] = useState('animal');

  // Auto-switch the tab to whichever category contains the currently saved avatar
  useEffect(() => {
    const a = AVATARS.find((x) => x.id === userProfile?.avatar_id);
    if (a?.category) setAvatarTab(a.category);
  }, [userProfile?.avatar_id]);

  // Single page, two views. Route determines which is active.
  // /user-profile → "profile" view (Profile + Company)
  // /settings     → "settings" view (Calendar + Job boards + Notifications)
  const activeTab = location.pathname.startsWith('/settings') ? 'settings' : 'profile';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(DEFAULT_AVATAR_ID);
  const [calendarConnecting, setCalendarConnecting] = useState(false);
  const [company, setCompany] = useState(null);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarProvider, setCalendarProvider] = useState(null);
  const [calendarEmail, setCalendarEmail] = useState(null);

  // ── Job-board publish defaults (admin only) ───────────────────
  const [publishDefaults, setPublishDefaults] = useState([]);
  const [savingPublishDefaults, setSavingPublishDefaults] = useState(false);

  const isAdmin = ['jobasti_admin', 'JADM', 'client_admin', 'CADM', 'RADM'].includes(userProfile?.role);
  const isRecruiter = ['client_admin', 'CADM', 'RADM', 'client_recruiter', 'CREC', 'RECR'].includes(userProfile?.role);
  const isSuperAdmin = ['jobasti_admin', 'JADM'].includes(userProfile?.role);
  const isOrgAdmin = ['client_admin', 'CADM', 'RADM'].includes(userProfile?.role);
  // Job-board section visibility: hidden from prospects + non-admin recruiters
  // (only org admins and super admins see it).
  const canSeeJobBoards = isOrgAdmin || isSuperAdmin;

  useEffect(() => {
    if (!isAdmin || !userProfile?.organizationId) return;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${userProfile.organizationId}/publish-defaults`)
      .then((r) => setPublishDefaults(r.data?.targets || []))
      .catch(() => setPublishDefaults([]));
  }, [isAdmin, userProfile?.organizationId]);

  const togglePublishDefault = (id) => {
    setPublishDefaults((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const savePublishDefaults = async () => {
    try {
      setSavingPublishDefaults(true);
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${userProfile.organizationId}/publish-defaults`,
        { targets: publishDefaults }
      );
      alert('Default job boards saved.');
    } catch (e) {
      alert('Failed to save defaults.');
    } finally {
      setSavingPublishDefaults(false);
    }
  };
  
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    location: ''
  });

  useEffect(() => {
    fetchData();
  }, [userProfile]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch user profile details
      if (userProfile) {
        setSelectedAvatar(userProfile.avatarUrl || userProfile.avatar_url || DEFAULT_AVATAR_ID);
        setProfileData({
          firstName: userProfile.firstName || userProfile.first_name || '',
          lastName: userProfile.lastName || userProfile.last_name || '',
          email: userProfile.email || '',
          phone: userProfile.phone || '',
          jobTitle: userProfile.jobTitle || userProfile.job_title || '',
          location: userProfile.location || ''
        });

        // Check if calendar is connected
        try {
          const calResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/calendar/status`,
            { params: { userId: userProfile.id } }
          );
          setCalendarConnected(calResponse.data?.connected || false);
          setCalendarProvider(calResponse.data?.provider || null);
          setCalendarEmail(calResponse.data?.email || null);
        } catch (err) {
          // Calendar endpoint may not exist yet
          setCalendarConnected(false);
        }

        // Fetch company details if user has an organization
        if (userProfile.organizationId || userProfile.organization_id) {
          try {
            const companyResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/company/${userProfile.organizationId || userProfile.organization_id}`
            );
            if (companyResponse.data?.success) {
              setCompany(companyResponse.data.data);
            }
          } catch (err) {
            console.error('Error fetching company:', err);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching settings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/${userProfile.id}`,
        profileData
      );
      alert('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarSave = async (avatarId) => {
    try {
      setSavingAvatar(true);
      setSelectedAvatar(avatarId);
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar`,
        { avatar_id: avatarId },
        { headers: { Authorization: `Bearer ${session?.access_token}` } }
      );
      await refreshProfile();
    } catch (error) {
      console.error('Error saving avatar:', error);
    } finally {
      setSavingAvatar(false);
    }
  };

  const handleConnectCalendar = async (provider = 'google') => {
    try {
      setCalendarConnecting(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/auth-url`,
        { params: { userId: userProfile.id, provider } }
      );

      if (response.data?.authUrl) {
        window.location.href = response.data.authUrl;
      } else {
        alert(`${provider === 'outlook' ? 'Outlook' : 'Google'} calendar integration is not configured.`);
      }
    } catch (error) {
      console.error('Error connecting calendar:', error);
      alert(error?.response?.data?.message || 'Calendar integration is not available yet.');
    } finally {
      setCalendarConnecting(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm('Are you sure you want to disconnect your calendar?')) return;
    
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/disconnect`,
        { userId: userProfile.id }
      );
      setCalendarConnected(false);
      alert('Calendar disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      alert('Failed to disconnect calendar');
    }
  };

  const getRoleName = (role) => {
    const roleMap = {
      'jobasti_admin': t('settings.role.superAdmin'),
      'JADM': t('settings.role.superAdmin'),
      'client_admin': t('settings.role.adminRecruiter'),
      'CADM': t('settings.role.adminRecruiter'),
      'RADM': t('settings.role.adminRecruiter'),
      'client_recruiter': t('settings.role.recruiter'),
      'CREC': t('settings.role.recruiter'),
      'RECR': t('settings.role.recruiter'),
      'prospect': t('settings.role.applicant'),
      'PROS': t('settings.role.applicant')
    };
    return roleMap[role] || role;
  };

  if (loading) {
    const loadingBody = (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-center text-gray-600">Loading settings...</p>
      </div>
    );
    return isRecruiter
      ? <RecruiterLayout>{loadingBody}</RecruiterLayout>
      : <div className="min-h-screen bg-gray-50"><ResponsiveNavigation />{loadingBody}</div>;
  }

  const settingsBody = (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="settings-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="page-title">
            {activeTab === 'profile' ? (
              <>
                <User className="w-6 h-6 text-blue-600" />
                {t('settings.profileTitle')}
              </>
            ) : (
              <>
                <SettingsIcon className="w-6 h-6 text-blue-600" />
                {t('settings.settingsTitle')}
              </>
            )}
          </h1>
          <p className="text-gray-600 mt-1">
            {activeTab === 'profile' ? t('settings.profileSubtitle') : t('settings.settingsSubtitle')}
          </p>
        </div>

        {/* Layout: nav + main in normal document flow (no fixed positioning).
            Both scroll together as the page scrolls. */}
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          {/* Left-side nav — sits inline as a normal sibling */}
          <aside
            className="w-full sm:w-56 flex-shrink-0"
            data-testid="account-side-nav-wrap"
          >
            <nav
              className="bg-white border border-gray-200 rounded-xl p-2"
              data-testid="account-side-nav"
            >
              <button
                onClick={() => navigate('/user-profile')}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                  activeTab === 'profile'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="side-nav-profile"
              >
                <User className="w-4 h-4" />
                {t('settings.sideNav.userProfile')}
              </button>
              <button
                onClick={() => navigate('/settings')}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition mt-1 ${
                  activeTab === 'settings'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="side-nav-settings"
              >
                <SettingsIcon className="w-4 h-4" />
                {t('settings.sideNav.settings')}
              </button>
            </nav>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-6" data-testid="account-main">
            {activeTab === 'profile' && (
              <>
                {/* ───────────── PROFILE DETAILS ───────────── */}
          <Card data-testid="profile-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                {t('settings.profile.cardTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6 mb-6">
                {/* Current Avatar */}
                <div className="flex-shrink-0">
                  <img
                    src={getAvatarUrl(selectedAvatar)}
                    alt="Avatar"
                    className="w-20 h-20 rounded-full object-cover ring-2 ring-blue-200"
                    data-testid="current-avatar"
                  />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {profileData.firstName} {profileData.lastName}
                  </h3>
                  <p className="text-gray-600">{profileData.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-600 font-medium">
                      {getRoleName(userProfile?.role)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Avatar Selection */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="avatar-picker">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700">{t('settings.profile.chooseAvatar')}</p>
                  {/* Category tabs */}
                  <div className="inline-flex bg-white border border-gray-200 rounded-lg p-0.5 text-xs">
                    <button
                      type="button"
                      onClick={() => setAvatarTab('animal')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
                        avatarTab === 'animal'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      data-testid="avatar-tab-animal"
                      title={t('settings.profile.animals')}
                    >
                      <PawPrint className="w-3.5 h-3.5" />
                      {t('settings.profile.animals')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAvatarTab('human')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
                        avatarTab === 'human'
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      data-testid="avatar-tab-human"
                      title={t('settings.profile.people')}
                    >
                      <User className="w-3.5 h-3.5" />
                      {t('settings.profile.people')}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  {AVATARS.filter((a) => a.category === avatarTab).map((avatar) => (
                    <button
                      key={avatar.id}
                      onClick={() => handleAvatarSave(avatar.id)}
                      disabled={savingAvatar}
                      className={`w-14 h-14 rounded-full overflow-hidden transition-all ${
                        selectedAvatar === avatar.id
                          ? 'ring-3 ring-blue-500 ring-offset-2 scale-110'
                          : 'ring-1 ring-gray-200 hover:ring-blue-300 hover:scale-105'
                      }`}
                      title={avatar.label}
                      data-testid={`avatar-option-${avatar.id}`}
                    >
                      <img src={avatar.url} alt={avatar.label} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                {savingAvatar && <p className="text-xs text-blue-500 mt-2">{t('settings.profile.saving')}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.profile.firstName')}
                  </label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    data-testid="profile-firstname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.profile.lastName')}
                  </label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    data-testid="profile-lastname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.profile.email')}
                  </label>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={profileData.email}
                      disabled
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.profile.phone')}
                  </label>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      placeholder={t('settings.profile.phonePlaceholder')}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      data-testid="profile-phone"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.profile.jobTitle')}
                  </label>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={profileData.jobTitle}
                      onChange={(e) => setProfileData({ ...profileData, jobTitle: e.target.value })}
                      placeholder={t('settings.profile.jobTitlePlaceholder')}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      data-testid="profile-jobtitle"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.profile.location')}
                  </label>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={profileData.location}
                      onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                      placeholder={t('settings.profile.locationPlaceholder')}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      data-testid="profile-location"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={handleProfileUpdate}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-profile-btn"
                >
                  {saving ? t('settings.profile.savingBtn') : t('settings.profile.saveChanges')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* ───────────── LANGUAGE (Amazon-style dropdown) ───────────── */}
          <Card data-testid="language-card" translate="no">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                {t('settings.language.cardTitle')}
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">{t('settings.language.description')}</p>
            </CardHeader>
            <CardContent>
              <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => setLangOpen((v) => !v)}
                  className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg bg-white hover:border-blue-400 hover:bg-blue-50/40 transition text-sm font-medium text-gray-900 shadow-sm"
                  data-testid="language-dropdown-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={langOpen}
                >
                  <span className="text-lg leading-none">
                    {SUPPORTED_LANGUAGES.find((l) => l.code === currentLang)?.flag || '🌐'}
                  </span>
                  <span>{SUPPORTED_LANGUAGES.find((l) => l.code === currentLang)?.label || 'English'}</span>
                  <span className="text-gray-400 text-xs ml-1">▾</span>
                </button>

                {langOpen && (
                  <div
                    className="absolute z-50 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                    role="listbox"
                    data-testid="language-dropdown-menu"
                  >
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                      <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                        {t('settings.language.current')}: {SUPPORTED_LANGUAGES.find((l) => l.code === currentLang)?.label}
                      </p>
                    </div>
                    <ul className="max-h-72 overflow-y-auto py-1">
                      {(langShowAll ? SUPPORTED_LANGUAGES : SUPPORTED_LANGUAGES.slice(0, 4)).map((lang) => (
                        <li key={lang.code}>
                          <button
                            type="button"
                            onClick={() => changeLanguage(lang.code)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-blue-50 transition ${
                              lang.code === currentLang ? 'bg-blue-50/60 text-blue-700 font-semibold' : 'text-gray-800'
                            }`}
                            data-testid={`language-option-${lang.code}`}
                          >
                            <span className="text-lg leading-none">{lang.flag}</span>
                            <span className="flex-1">{lang.label}</span>
                            {lang.code === currentLang && <CheckIcon className="w-4 h-4 text-blue-600" />}
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-gray-100 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setLangShowAll((v) => !v)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        data-testid="language-toggle-all"
                      >
                        {langShowAll ? t('settings.language.showLess') : t('settings.language.moreLanguages')}
                      </button>
                    </div>
                  </div>
                )}

                {langSaved && (
                  <span className="ml-3 inline-flex items-center gap-1 text-xs text-emerald-600 font-medium" data-testid="language-saved-toast">
                    <CheckIcon className="w-3.5 h-3.5" />
                    {t('settings.language.saved')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Company Section */}
          {company && (
            <Card data-testid="company-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  {t('settings.company.cardTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t('settings.company.companyName')}
                    </label>
                    <p className="text-gray-900 font-medium">{company.name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t('settings.company.industry')}
                    </label>
                    <p className="text-gray-900">{company.industry || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t('settings.company.location')}
                    </label>
                    <p className="text-gray-900 flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      {company.city || company.address || 'N/A'}
                      {company.country && `, ${company.country}`}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t('settings.company.website')}
                    </label>
                    {company.website ? (
                      <a 
                        href={company.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Link2 className="w-4 h-4" />
                        {company.website}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <p className="text-gray-900">N/A</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t('settings.company.contactEmail')}
                    </label>
                    <p className="text-gray-900 flex items-center gap-1">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {company.email || company.contact_email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t('settings.company.phone')}
                    </label>
                    <p className="text-gray-900 flex items-center gap-1">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {company.phone || company.contact_phone || 'N/A'}
                    </p>
                  </div>
                </div>

                {company.description && (
                  <div className="mt-4 pt-4 border-t">
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t('settings.company.about')}
                    </label>
                    <p className="text-gray-700 text-sm leading-relaxed">{company.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

              </>
            )}

            {activeTab === 'settings' && (
              <>
                {/* ───────────── SETTINGS TAB ───────────── */}

                {/* Calendar Integration Section */}
          <Card data-testid="calendar-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                {t('settings.calendar.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {calendarConnected ? (
                /* Connected state — single panel for whichever provider is active */
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {calendarProvider === 'outlook' ? (
                        <img src="https://img.icons8.com/color/48/microsoft-outlook-2019.png" alt="Outlook" className="w-8 h-8 object-contain" />
                      ) : (
                        <img src="https://www.gstatic.com/images/branding/product/1x/calendar_2020q4_48dp.png" alt="Google Calendar" className="w-8 h-8" />
                      )}
                      <div>
                        <h4 className="font-medium text-gray-900">{calendarProvider === 'outlook' ? 'Outlook Calendar' : 'Google Calendar'}</h4>
                        <p className="text-sm text-gray-500">{t('settings.calendar.syncDesc')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-600 font-medium text-sm">{calendarEmail ? t('settings.calendar.connectedAs', { email: calendarEmail }) : t('settings.calendar.connected')}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleDisconnectCalendar}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="disconnect-calendar-btn"
                  >
                    {t('settings.calendar.disconnect')}
                  </Button>
                </div>
              ) : (
                /* Disconnected state — show both providers side-by-side */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="calendar-providers">
                  {[
                    { id: 'google', name: 'Google Calendar', img: 'https://www.gstatic.com/images/branding/product/1x/calendar_2020q4_48dp.png', desc: t('settings.calendar.googleDesc') },
                    { id: 'outlook', name: 'Outlook Calendar', img: 'https://img.icons8.com/color/48/microsoft-outlook-2019.png', desc: t('settings.calendar.outlookDesc') },
                  ].map(p => (
                    <button
                      key={p.id}
                      onClick={() => handleConnectCalendar(p.id)}
                      disabled={calendarConnecting}
                      className="border border-gray-200 hover:border-blue-400 hover:bg-blue-50/40 rounded-lg p-4 text-left transition disabled:opacity-50"
                      data-testid={`connect-${p.id}-btn`}
                    >
                      <div className="flex items-center gap-3">
                        <img src={p.img} alt={p.name} className="w-8 h-8 object-contain flex-shrink-0" />
                        <div className="min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm">{p.name}</h4>
                          <p className="text-xs text-gray-500 mt-0.5">{p.desc}</p>
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 mt-2 font-medium">{calendarConnecting ? t('settings.calendar.connecting') : t('settings.calendar.connectArrow')}</p>
                    </button>
                  ))}
                </div>
              )}

              {calendarConnected && (
                <div className="mt-4 pt-4 border-t">
                  <h5 className="font-medium text-gray-700 mb-2 text-sm">{t('settings.calendar.syncTitle')}</h5>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input type="checkbox" defaultChecked className="rounded text-blue-600" />
                      {t('settings.calendar.showEvents')}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input type="checkbox" defaultChecked className="rounded text-blue-600" />
                      {t('settings.calendar.syncInterviews')}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input type="checkbox" className="rounded text-blue-600" />
                      {t('settings.calendar.twoWaySync')}
                    </label>
                  </div>
                </div>
              )}

              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>{t('settings.calendar.noteStrong')}</strong> {t('settings.calendar.noteBody')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Job Board Defaults (org admin only — not relevant to Jobasti Super Admin) */}
          {isOrgAdmin && (
            <Card data-testid="job-board-defaults-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Send className="w-5 h-5 text-blue-600" />
                  {t('settings.jobBoards.defaultsTitle')}
                </CardTitle>
                <p className="text-xs text-gray-500 mt-1">
                  {t('settings.jobBoards.defaultsDesc')}
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {JOB_BOARDS.map((b) => {
                    const isOn = publishDefaults.includes(b.id);
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => togglePublishDefault(b.id)}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left transition ${isOn ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                        data-testid={`job-board-default-${b.id}`}
                      >
                        {b.inlineGlyph && BrandLogos[b.inlineGlyph] ? (
                          React.createElement(BrandLogos[b.inlineGlyph], { size: 28 })
                        ) : b.logoUrl ? (
                          <img src={b.logoUrl} alt={b.name} className="w-7 h-7 object-contain" loading="lazy" />
                        ) : (
                          <div className="w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: b.color }}>
                            {b.name.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{b.name}</p>
                          <p className="text-[10px] text-gray-500">{b.country}</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={isOn}
                          readOnly
                          className="rounded text-blue-600 w-4 h-4 pointer-events-none"
                        />
                      </button>
                    );
                  })}
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <p className="text-[11px] text-amber-600 font-medium">
                    {t('settings.jobBoards.pendingNote')}
                  </p>
                  <Button
                    onClick={savePublishDefaults}
                    disabled={savingPublishDefaults}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="job-board-defaults-save"
                  >
                    {savingPublishDefaults ? t('settings.jobBoards.saving') : t('settings.jobBoards.saveDefaults')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Company's own job-board credentials (org admins + super admins) */}
          {canSeeJobBoards && (
            userProfile?.organizationId ? (
              <CompanyJobBoardsCard organizationId={userProfile.organizationId} />
            ) : isSuperAdmin ? (
              <Card data-testid="company-job-boards-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold">{t('settings.jobBoards.companyAccountsTitle')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p
                    className="text-sm text-gray-500"
                    dangerouslySetInnerHTML={{ __html: t('settings.jobBoards.companyAccountsSuperAdminNote') }}
                  />
                </CardContent>
              </Card>
            ) : null
          )}

          {/* Platform-managed feeds card removed — not relevant to Super Admin */}

          {/* Notification Preferences */}
          <Card data-testid="notifications-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                {t('settings.notifications.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{t('settings.notifications.newApplication')}</span>
                  <input type="checkbox" defaultChecked className="rounded text-blue-600 w-5 h-5" />
                </label>
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{t('settings.notifications.interviewReminders')}</span>
                  <input type="checkbox" defaultChecked className="rounded text-blue-600 w-5 h-5" />
                </label>
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{t('settings.notifications.candidateStatus')}</span>
                  <input type="checkbox" defaultChecked className="rounded text-blue-600 w-5 h-5" />
                </label>
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{t('settings.notifications.weeklySummary')}</span>
                  <input type="checkbox" className="rounded text-blue-600 w-5 h-5" />
                </label>
              </div>
            </CardContent>
          </Card>
              </>
            )}
          </div>
        </div>
      </div>
  );

  if (isRecruiter) return <RecruiterLayout>{settingsBody}</RecruiterLayout>;
  return (
    <div className="min-h-screen bg-gray-50">
      <ResponsiveNavigation />
      {settingsBody}
    </div>
  );
};

export default Settings;
